
import appStore from "@/utils/store";

export const LANGUAGES = [
    { code: "en", label: "English", flag: "/assets/img/flags/en.svg" },
    { code: "es", label: "Español ES", flag: "/assets/img/flags/es.svg" },
    { code: "eslat", label: "Español LATAM", flag: "/assets/img/flags/mx.svg" },
    { code: "ca", label: "Català", flag: "/assets/img/flags/ca.svg" },
    { code: "fr", label: "Français", flag: "/assets/img/flags/fr.svg" },
    { code: "it", label: "Italiano", flag: "/assets/img/flags/it.svg" },
    { code: "pt", label: "Português", flag: "/assets/img/flags/pt.svg" },
    { code: "ga", label: "Galego", flag: "/assets/img/flags/ga.svg" }
];

function formatBrowserLanguage(browserLanguage: string): string {
    try {
        // Normaliza el idioma a minúsculas
        const normalizedLang = browserLanguage.toLowerCase();

        // Define los idiomas soportados
        const supportedLanguages: Record<string, string> = {
            en: 'en', // Inglés
            es: 'es', // Español
            fr: 'fr', // Francés
            it: 'it', // Italiano
            pt: 'pt', // Portugués
            ca: 'ca', // Catalán
            ga: 'ga', // Gallego
            'es-mx': 'eslat', // Español de México
            'es-ar': 'eslat', // Español de Argentina
            'es-co': 'eslat', // Español de Colombia
            'es-cl': 'eslat', // Español de Chile
            'es-pe': 'eslat', // Español de Perú
            'es-xl': 'eslat', // Español Latinoamericano
            'es-lt': 'eslat', // Español Latinoamericano de otros países
        };

        // Encuentra el idioma soportado
        for (const key in supportedLanguages) {
            if (normalizedLang.startsWith(key)) {
                return supportedLanguages[key] || 'es'; // Retorna el idioma encontrado o 'es' por defecto
            }
        }
    } catch {
        // En caso de error, retorna el idioma predeterminado
        return 'es';
    }

    // Caso predeterminado si no encuentra un idioma soportado
    return 'es';
}
export function getUserLanguage(): string {
    const user = appStore.get("user");
    const temporaryLang = localStorage.getItem("temporaryLang") as string || localStorage.getItem("language") as string;

    // @ts-ignore
    const nav = navigator.language;
    const lang =
        user && user.language ? user.language :
            temporaryLang && temporaryLang !== '' ? temporaryLang : formatBrowserLanguage(nav);

    // console.log("🌐 User language:", user?.language, "LocalStorage:", localStorage.getItem("language"), "Navigator:", getLocaleFromNavigator());
    return lang;
}

export function setUserLanguage(code: string) {
    localStorage.setItem("language", code);
}

export function mapAppLangToIntlLocale(lang: string): string {
    switch (lang) {
        case "eslat": return "es"; // LATAM
        case "ga": return "es";    // Gallego
        case "ca": return "en";    // Catalán
        default: return lang;      // es, en, fr, etc.
    }
}

export function getSingleLetterDay(date: Date, lang: string): string {
    const day = date.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday

    const map = {
        es: ['D', 'L', 'M', 'X', 'J', 'V', 'S'],
        en: ['U', 'M', 'T', 'W', 'R', 'F', 'S'],
        fr: ['D', 'L', 'M', 'C', 'J', 'V', 'S'],
        it: ['D', 'L', 'M', 'M', 'G', 'V', 'S'],
        pt: ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'],
        ca: ['D', 'L', 'M', 'X', 'J', 'V', 'S'],
        ga: ['D', 'L', 'M', 'M', 'X', 'V', 'S'],
        eslat: ['D', 'L', 'M', 'X', 'J', 'V', 'S']
    };
    try {
        const letters = map[lang] || map['en'];
        // console.log(`🌐 Single letter day for ${lang}:`, letters[day]);
        return letters[day];
    } catch (error) {
        console.error("Error getting single letter day:", error);
    }

    // Old way to do it:
    return new Date(day)
        .toLocaleDateString(
            mapAppLangToIntlLocale(getUserLanguage() || "en"),
            {
                weekday: "short",
            },
        )
        .toUpperCase()
        .charAt(0);

}

/**
 * Convierte un locale de navegador a nuestro code interno.
 * @param locale Ej.: "es-ES", "en-US", "fr-FR"
 * @returns Uno de nuestros codes: es, eslat, en, fr...
 */
export function matchLocaleToCode(locale: string): string {
    if (!locale) return "en";

    const normalized = locale.toLowerCase();

    if (normalized.startsWith("es-")) {
        // España vs Latinoamérica
        if (["es-es"].includes(normalized)) return "es";
        return "eslat";
    }

    if (normalized.startsWith("en")) return "en";
    if (normalized.startsWith("fr")) return "fr";
    if (normalized.startsWith("ca")) return "ca";
    if (normalized.startsWith("it")) return "it";
    if (normalized.startsWith("pt")) return "pt";
    if (normalized.startsWith("gl") || normalized.startsWith("ga")) return "ga";

    // Fallback
    return "en";
}

/**
 * Devuelve el nombre completo del idioma para usar en prompts.
 * @param code Nuestro code interno.
 * @returns Ej.: "Español de España", "Inglés", etc.
 */
export function getLanguageLabel(code: string): string {
    switch (code) {
        case "es": return "Español de España";
        case "eslat": return "Español Latinoamericano";
        case "en": return "Inglés";
        case "fr": return "Francés";
        case "ca": return "Catalán";
        case "it": return "Italiano";
        case "pt": return "Portugués";
        case "ga": return "Gallego";
        default: return "Inglés";
    }
}

const LANGUAGE = { latinoamericano: '0c640484e99cd30ea5726443a5ce9ac3567c849e9b5d7165ad568d1df96a3dee', gallego: 'bf2cbc9cb6d8c1d3dde11c9e877f9871b64de5138e6cceae281f52435f096ee2', catalan: 'd903ef260c734cf5dee3fa4cfaf59769569f6fe5596c36eff105e0c0631de3e5', portuges: 'c64c70b517e1e62df1e5e974cdf9d177f4c2c061cf4c10e261ef93116d737cc1', flamenco: 'a01fd34b06e9769945d58544f63c71c853f143f0c7f029fb7d1e5439dcdf6b5c', french: '99b3afaffea0425e0caaaeddc132e39be27e5e6029e87986010e9d3177c85c5d', italian: 'ac1f321740e885a8be9437400e3c06248c63da3c27b03f562c6a807a0e62781d', english: 'a4bd9b6abaceaf54db7cb2f5dbf979b4566e4229a42b1dc44a0145c147e54720', espaniol: 'default' };
export function chooseOrbbyteLang() {
    switch (getUserLanguage()) {
        case 'ca': return LANGUAGE.catalan;
        case 'ga': return LANGUAGE.gallego;
        case 'es': return LANGUAGE.espaniol;
        case 'en': return LANGUAGE.english;
        case 'pt': return LANGUAGE.portuges;
        case 'fr': return LANGUAGE.french;
        case 'it': return LANGUAGE.italian;
        case 'eslat':
        case 'es-lat': return LANGUAGE.latinoamericano;
        default:
            return LANGUAGE.espaniol;
    }
}

export function mapAppLangToSpeechLang(lang: string): string {
    switch (lang) {
        case "es":
            return "es-ES"; // Español de España
        case "eslat":
            return "es-419"; // Español latinoamericano
        case "ca":
            return "ca-ES"; // Catalán
        case "en":
            return "en-US"; // Inglés (EE.UU. como opción estándar)
        case "fr":
            return "fr-FR"; // Francés (Francia)
        case "it":
            return "it-IT"; // Italiano
        case "pt":
            return "pt-PT"; // Portugués (Portugal)
        case "ga":
            return "gl-ES"; // Gallego (aunque no aparece en el listado soportado, puedes usar "es-ES" como fallback si no funciona)
        default:
            return "en-US"; // Fallback general
    }
}